// @flow
import { isDOMTypeElement } from 'Common/checkReactElementsTypes';
import isNumberRange from 'Common/isNumberRange';

/**
 * It generates an js error
 * @param {string} stringError - error to pass to the Error instance
 * @return {object} a new Error
 */
export const getError = (stringError: string) => {
  throw new Error(stringError);
};

export const viewports = ['xs', 'sm', 'md', 'lg'];
export const newViewports = [
  { xs: '(min-width:0px' },
  { sm: '(min-width: 768px)' },
  { md: '(min-width: 992px)' },
  { lg: '(min-width: 1200px)' }
];

const hayPropTypes = (() => {
  /**
   * It checks the types.
   * @param {object} elem - React component
   * @param {string} typesToCheck - component's type
   * @return {boolean} -
   */
  const isCorrectComponentTypes = (elem, typesToCheck) => {
    if (elem == null) {
      return getError('Should have at least 1 child component');
    }

    const ArrTypesToCheck = Array.isArray(typesToCheck)
      ? typesToCheck
      : [typesToCheck];

    // At least one type has to be true
    return !!ArrTypesToCheck.some(currType => {
      // "elem.type.type"" is for React components create with React.createElement.
      // We need to consider also if the component is connected, therefore, in that case, we have to check the WrappedComponent.name
      return (
        elem.type &&
        (elem.type.type === currType ||
          elem.type === currType ||
          (elem.type.name === 'Connect' &&
            elem.type.WrappedComponent &&
            elem.type.WrappedComponent.name === currType) ||
          (elem.type.name !== 'Connect' && elem.type.name === currType))
      );
    });
  };

  /**
   * It performs common checks on the prop
   * @param {object} props - from PropTypes
   * @param {string} propName - prop's name
   * @param {string} componentName - component's name
   * @param {number} numberOfChildren - enforce the check on the number of children supplied
   * @return {null} - null for successful validation or throws an error
   */
  const commonCheck = (props, propName, componentName, numberOfChildren) => {
    let childrenProps = props[propName];

    if (childrenProps) {
      if (isDOMTypeElement(childrenProps)) {
        childrenProps = childrenProps.props.children;
      }

      const children = Array.isArray(childrenProps)
        ? childrenProps
        : [childrenProps];

      if (numberOfChildren && children.length > numberOfChildren) {
        getError(
          `${componentName}'s prop children: Only ${numberOfChildren} can be passed. You are passing: ${children.length}`
        );
      }

      return children;
    }

    return null;
  };

  /**
   * It checks the truthiness of the children prop
   * @param {object} props - from PropTypes
   * @param {string} propName - prop's name
   * @param {string} componentName - component's name
   * @param {string} types - types to check
   * @param {number} numberOfChildren - enforce the check on the number of children supplied
   * @return {null} - null for successful validation or throws an error
   */
  const checkTruthiness = (
    props: Object,
    propName: string,
    componentName: string,
    types: Array<string> | string,
    numberOfChildren: number | null = null
  ) => {
    const children = commonCheck(
      props,
      propName,
      componentName,
      numberOfChildren
    );

    if (children === null) {
      return null;
    }

    return children.every(child => {
      if (!isCorrectComponentTypes(child, types)) {
        return false;
      }

      return true;
    })
      ? null
      : getError(
          `${componentName}'s prop ${propName} has to be of type: ${types.toString()}`
        );
  };
  /**
   * It checks the falsiness of the children prop
   * @param {object} props - from PropTypes
   * @param {string} propName - prop's name
   * @param {string} componentName - component's name
   * @param {string} types - types to check
   * @param {number} numberOfChildren - enforce the check on the number of children supplied
   * @return {null} - null for successful validation or throws an error
   */
  const checkFalsiness = (
    props: Object,
    propName: string,
    componentName: string,
    types: Array<string> | string,
    numberOfChildren: number | null = null
  ) => {
    const children = commonCheck(
      (props: Object),
      (propName: string),
      (componentName: string),
      (numberOfChildren: number | null)
    );

    if (children === null) {
      return null;
    }

    return children.every(child => {
      if (isCorrectComponentTypes(child, types)) {
        return false;
      }

      return true;
    })
      ? null
      : getError(
          `${componentName}'s prop ${propName} cannot be of type: ${types.toString()}`
        );
  };

  /**
   * It checks that elem is in ArrTypesToCheck.
   * @param {Array} ArrTypesToCheck - Array passed to check against
   * @param {*} elem - current element to check against ArrTypesToCheck
   * @return {boolean} -
   */
  const validateArray = (ArrTypesToCheck: Array<string>, elem: string) => {
    return ArrTypesToCheck.some(currType => {
      return elem === currType;
    });
  };

  /**
   * This is the private function called when types is passed to the React propTypes arrayOf (i.e. PropTypes.arrayOf(hayPropTypes.types(viewports))). arrayOf goes throw every element of the array and you need to provide a function which does the comparison that you need.
   * @param {*} propValue - from arrayOf
   * @param {*} key - from arrayOf
   * @param {*} componentName - from arrayOf
   * @param {*} location - from arrayOf
   * @param {*} propFullName - from arrayOf
   * @param {*} ArrTypesToCheck - Array to check against.
   * @return {error} - It returns error only if the validation fails.
   */
  const checkArrayOfTypes = (
    propValue: Object,
    key: string,
    componentName: string,
    location: string,
    propFullName: string,
    ArrTypesToCheck: Array<string>
  ) => {
    if (!validateArray(ArrTypesToCheck, propValue[key])) {
      const value = propValue[key];
      getError(
        `Validation failed. Invalid ${location} ${propFullName}. '${value}' supplied to ${componentName}, but it needs to match a value in the array [${ArrTypesToCheck.toString()}]`
      );
    }
  };

  return {
    // Only 1 Child is allowed and it can be ONLY of a specific type
    childOfType: (type: Array<string> | string) => {
      return (props: Object, propName: string, componentName: string) => {
        return checkTruthiness(props, propName, componentName, type, 1);
      };
    },

    // Only 1 Child is allowed and it can be ONLY of some specific types
    childOfTypes: (types: Array<string>) => {
      return (props: Object, propName: string, componentName: string) => {
        return checkTruthiness(props, propName, componentName, types, 1);
      };
    },

    // Children MUST be of only specific types
    childrenOfTypes: (types: Array<string> | string) => {
      return (props: Object, propName: string, componentName: string) => {
        return checkTruthiness(props, propName, componentName, types);
      };
    },

    // Only a specific number of elements (null is any) are allowed and only of specific types
    onlySpecificNumberOfOfTypes: (
      types: Array<string> | string,
      numberOfChildren: number | null = null
    ) => {
      return (props: Object, propName: string, componentName: string) => {
        return checkTruthiness(
          (props: Object),
          (propName: string),
          (componentName: string),
          (types: Array<string> | string),
          (numberOfChildren: number | null)
        );
      };
    },

    notAllowChildrenOfTypes: (types: Array<string> | string) => {
      return (props: Object, propName: string, componentName: string) => {
        return checkFalsiness(props, propName, componentName, types);
      };
    },

    // NOTE: Not to be used as stand-alone, but only in conjunction with PropTypes.arrayOf -> PropTypes.arrayOf(types(['whatever1','whatever2']))
    types: (ArrTypesToCheckAgainst: Array<string>) => {
      return (
        propValue: Object,
        key: string,
        componentName: string,
        location: string,
        propFullName: string
      ) => {
        return checkArrayOfTypes(
          propValue,
          key,
          componentName,
          location,
          propFullName,
          ArrTypesToCheckAgainst
        );
      };
    },

    numberInRange: (min: number, max: number) => {
      return (props: Object, propName: string, componentName: string) => {
        const suppliedPropValue = props[propName];
        return isNumberRange(props[propName], min, max)
          ? null
          : getError(
              `Validation failed. Invalid prop ${propName}. '${suppliedPropValue}' supplied in ${componentName}, but it needs to be a number beetwen ${min} and ${max}.`
            );
      };
    },

    // It allows to pass a callback in order to check a related prop. This is useful when you need to validate two
    // props that are related i.e. in the grid system offset + col has to be < 13 and offset and col are two props.
    checkRelatedProp: (
      callback: Function,
      relatedPropName: string,
      errorMsg: string = ''
    ) => {
      return (props: Object, propName: string, componentName: string) => {
        const relPropName = props[relatedPropName];
        return callback(props, propName, componentName, relatedPropName) ===
          true
          ? null
          : getError(
              `Validation failed. Invalid prop ${propName}. '${relPropName}' supplied in ${componentName}. ${errorMsg}`
            );
      };
    }
  };
})();

export default hayPropTypes;



// WEBPACK FOOTER //
// ./src/proptypes/hayPropTypes/index.js